/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnInit, Component, ElementRef, Renderer2 } from '@angular/core';
import { CoreEventServiceKeyE, EventService, I18nService, LayoutStyleService, TimeService, UtilService } from '@dm/core';
import { UNPLACEHOLDER } from 'app/common/constants';
import { ShardSwitch, timeZones } from 'app/config';
import { type Subscription } from 'rxjs';

@Component({
    selector: 'app-login-info',
    templateUrl: './login-info.component.html',
    styleUrls: ['./login-info.component.scss'],
})
export class LoginInfoComponent implements OnInit, AfterViewInit {
    i18n = {
        title: this.i18nService.get('common.login_task_loginSuccess_title'),
        lastLoginTime: this.i18nService.get('common.login_task_loginsuccess_lastLoginTime_lab', true),
        lastLoginIP: this.i18nService.get('common.login_task_loginsuccess_lastLoginIP_lab', true),
    };

    // 显示登录提示
    show = false;

    // 登录提示
    loginInfo;

    /**
     * 登陆信息top值
     */
    loginInfoTopValue: string;
    /**
     * 事件服务中订阅的事件
     */
    events: Subscription[] = [];

    // 时区列表全集
    timeZones;

    // 自动关闭
    closeTimer = null;

    ShardSwitch = ShardSwitch;

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private i18nService: I18nService,
        private eventService: EventService,
        private timeService: TimeService,
        private layoutStyleService: LayoutStyleService,
        public utilService: UtilService,
    ) {
        this.timeZones = timeZones[i18nService.language];
    }

    ngOnInit(): void {
        this.eventService.subscribe('showLoginInfo', (res) => {
            this.initContent(res);
        });

        // 显示前调整top值，登陆信息贴合header下发，避免遮挡告警等信息
        this.loginInfoTopValue = this.layoutStyleService.getHeightOfHeaderAndTipsOverHeader();
        // 显示过程中监听header上方提示栏的变化调整top值
        const checkTipsOverHeader$ = this.eventService.subscribe(CoreEventServiceKeyE.CHECK_TIPS_OVER_HEADER, () => {
            this.loginInfoTopValue = this.layoutStyleService.getHeightOfHeaderAndTipsOverHeader();
        });
        this.events.push(checkTipsOverHeader$);
    }

    ngAfterViewInit(): void {
        this.listenHover();
    }

    // 初始化提示信息
    initContent(data): void {
        const { showLast = false, showCustom = false, lastInfo = [], customInfo = [] } = data;
        const timeZoneConfig = this.timeService.getTimeZoneConfig();

        this.loginInfo = {
            last: {
                show: showLast,
                loginTime: this.formatLoginTime(lastInfo[0], timeZoneConfig),
                loginIP: lastInfo[1] || UNPLACEHOLDER,
            },
            custom: {
                show: showCustom,
                content: customInfo[0],
            },
        };

        // 延迟1s，再显示登录信息
        setTimeout(() => {
            if (ShardSwitch.SHOW_CUSTOM_PROMPT) {
                this.show = showLast;
            } else {
                this.show = showLast || showCustom;
            }

            this.closeSelf();
        }, 1000);
    }

    formatLoginTime(lastLoginTime, timeZoneConfig): string {
        const timeFormated = this.timeService.formatUTC(lastLoginTime * 1000, undefined, true);

        const timeZone = this.timeZones.find((item) => item.value === timeZoneConfig.timeZoneName);
        const zoneLabel = timeZone ? timeZone.label.substring(9) : UNPLACEHOLDER;
        if (lastLoginTime === 0) {
            return UNPLACEHOLDER;
        } else {
            return `${timeFormated} ${zoneLabel}`;
        }
    }

    // 5s后自动隐藏
    closeSelf(): void {
        if (this.closeTimer) {
            clearTimeout(this.closeTimer);
        }

        this.closeTimer = setTimeout(() => {
            this.show = false;
            this.events.forEach((event) => {
                event.unsubscribe();
            });
        }, 5 * 1e3);
    }

    // 鼠标悬浮事件
    listenHover(): void {
        // 鼠标悬浮在提示上时，取消自动关闭
        this.renderer.listen(this.elementRef.nativeElement, 'mouseenter', () => {
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
            }
        });

        // 鼠标移除后，5s自动关闭
        this.renderer.listen(this.elementRef.nativeElement, 'mouseleave', () => {
            this.closeSelf();
        });
    }
}
